import React, { useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Tag, Tooltip } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import IrenaIndicatorMappingModal from 'components/modals/IrenaIndicatorMappingModal';

const { Text } = Typography;
const { Search } = Input;

export default function IrenaIndicatorMappingTab({ indicatorMappings, onRefreshIndicatorMappings }) {
  const [loading, setLoading] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [query, setQuery] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const data = indicatorMappings || [];

  const handleCheck = (id) => async () => {
    setCheckingId(id);
    try {
      const res = await AxiosOICStat.post(`/external_api/irena/check-indicator/${id}`);
      const newStatus = res.data?.status;
      onRefreshIndicatorMappings();
      message.success(newStatus ? 'API data available' : 'No data from API');
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Check failed');
    } finally { setCheckingId(null); }
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const res = await AxiosOICStat.post('/external_api/irena/check-all-indicators');
      const results = res.data?.results || [];
      onRefreshIndicatorMappings();
      const ok = results.filter(r => r.status === true).length;
      const fail = results.filter(r => r.status === false).length;
      message.success(`Checked all: ${ok} available, ${fail} unavailable`);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Check all failed');
    } finally { setCheckingAll(false); }
  };

  const handleDelete = (id) => () => {
    Modal.confirm({ title: 'Delete Mapping', content: 'Delete this mapping?', onOk: async () => {
      try {
        await AxiosOICStat.delete(`/external_api/irena/indicator-mappings/${id}`);
        onRefreshIndicatorMappings();
        message.success('Deleted');
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Delete failed';
        message.error(detail);
      }
    }});
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    return data.filter(r => {
      const oicCode = String(r.oic_ind_code || '');
      const oicName = String(r.oic_ind_name || '').toLowerCase();
      const irenaCode = String(r.irena_indicator_code || '').toLowerCase();
      const table = String(r.irena_table || '').toLowerCase();
      const tech = String(r.technology_type || '').toLowerCase();
      return oicCode.includes(q) || oicName.includes(q) || irenaCode.includes(q) || table.includes(q) || tech.includes(q);
    });
  }, [data, query]);

  const columns = [
    {
      title: 'OIC Code',
      dataIndex: 'oic_ind_code',
      key: 'oic_ind_code',
      width: 80,
      sorter: (a, b) => (Number(a.oic_ind_code) || 0) - (Number(b.oic_ind_code) || 0),
      render: (v) => <Text strong>{v}</Text>
    },
    {
      title: 'OIC Indicator Name',
      dataIndex: 'oic_ind_name',
      key: 'oic_ind_name',
      width: 220,
      sorter: (a, b) => (String(a.oic_ind_name || '')).localeCompare(String(b.oic_ind_name || '')),
      render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Technology',
      dataIndex: 'technology_type',
      key: 'technology_type',
      width: 180,
      render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Tech Codes',
      dataIndex: 'technology_codes',
      key: 'technology_codes',
      width: 80,
      render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 60,
      render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      sorter: (a, b) => {
        const sa = a.status === true ? 1 : a.status === false ? -1 : 0;
        const sb = b.status === true ? 1 : b.status === false ? -1 : 0;
        return sb - sa;
      },
      render: (v, record) => {
        const timeStr = record.last_check_time ? new Date(record.last_check_time).toLocaleString() : null;
        if (v === true) return <Space><Tag icon={<CheckCircleOutlined />} color="success">Available</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        if (v === false) return <Space><Tag icon={<CloseCircleOutlined />} color="error">Unavailable</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        return <Tag icon={<QuestionCircleOutlined />} color="default">Unknown</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Check API for data availability">
            <Button
              icon={checkingId === record.id ? <SyncOutlined spin /> : <CheckCircleOutlined />}
              size="small"
              onClick={handleCheck(record.id)}
              loading={checkingId === record.id}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => { setEditingRecord(record); setAddModalVisible(true); }}
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={handleDelete(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Search allowClear placeholder="Search by indicator / IRENA code / table / technology…" onSearch={setQuery} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 420 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModalVisible(true); setEditingRecord(null); }}>Add New Indicator Mapping</Button>
        <Button icon={checkingAll ? <SyncOutlined spin /> : <CheckCircleOutlined />} onClick={handleCheckAll} loading={checkingAll}>Check All</Button>
      </Space>

      <Table rowKey={(r) => r.id || r.irena_indicator_code} loading={loading} columns={columns} dataSource={filtered} pagination={{ pageSize: 20, showSizeChanger: true }} bordered />
      <IrenaIndicatorMappingModal
        visible={addModalVisible}
        onCancel={() => { setAddModalVisible(false); setEditingRecord(null); }}
        onSuccess={() => { setAddModalVisible(false); setEditingRecord(null); onRefreshIndicatorMappings(); }}
        record={editingRecord}
      />
    </Space>
  );
}
